import { MenuItem, Select } from '@mui/material'
import * as Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import get from '../helpers/Api'
import { formatNumber, formatUSDM } from '../helpers/FormatLinks'
import LoadingIndicator from '../loadingindicator'
const Chart = (props) => {
    const { t } = useTranslation('common')
    const [isLoading, setIsLoading] = useState(true)
    const [revenueHistory, setRevenueHistory] = useState(null)
    const [chartRange, setChartRange] = useState(60)
    const searchAuthor = props['searchAuthor']
    const embedded = props['embedded']
    const [currency, setCurrency] = useState('usd')

    const GetRevenueHistory = async (range, author) => {
        setIsLoading(true)

        setChartRange(range)
        if (author) {
            get(
                'sales-volume-graph/' + chartRange + '/' + author + `?type=all`,
                'v3',
            ).then((res) => setRevenueHistory(res))
        } else {
            get('sales-volume-graph/' + chartRange + `?type=all`, 'v3').then(
                (res) => setRevenueHistory(res),
            )
        }

        setIsLoading(false)
    }

    useEffect(() => {
        GetRevenueHistory(chartRange, searchAuthor)
    }, [chartRange, searchAuthor])

    const chartDropDownOptions = [
        { value: 15, label: t('analytics.day_history', { days: 15 }) },
        { value: 30, label: t('analytics.day_history', { days: 30 }) },
        { value: 60, label: t('analytics.day_history', { days: 60 }) },
        { value: 90, label: t('analytics.day_history', { days: 90 }) },
        { value: 120, label: t('analytics.day_history', { days: 120 }) },
        { value: 180, label: t('analytics.day_history', { days: 180 }) },
        { value: 270, label: t('analytics.day_history', { days: 270 }) },
        { value: 365, label: t('analytics.day_history', { days: 365 }) },
    ]

    const currencyDropDownOptions = [
        { value: 'wax', label: 'WAX' },
        { value: 'usd', label: 'USD' },
    ]

    const selectCurrency = (e) => {
        setCurrency(e.target.value)
    }

    const getCurrencyOption = (search) => {
        return currencyDropDownOptions.map((item) => item.value).indexOf(search)
    }

    const defaultCurrencyOption =
        currencyDropDownOptions[getCurrencyOption(currency)]

    const getChartOption = (search) => {
        return chartDropDownOptions.map((item) => item.value).indexOf(search)
    }

    const onSelectChartRange = (e) => {
        GetRevenueHistory(e.target.value)
    }

    const defaultChartOption = chartDropDownOptions[getChartOption(chartRange)]

    if (!revenueHistory)
        return (
            <div>
                <LoadingIndicator />
            </div>
        )

    const getMaxSales = (sales) => {
        return Math.max(...sales) * 2.0
    }

    const chartOptions = revenueHistory
        ? {
              title: {
                  text: '',
              },
              chart: {
                  renderTo: 'Chart',
                  backgroundColor: 'rgba(0, 0, 0, 0)',
                  marginTop: 70,
                  marginBottom: 70,
                  marginLeft: 90,
                  marginRight: 90,
              },
              plotOptions: {
                  areaspline: {
                      marker: {
                          enabled: false,
                      },
                  },
                  column: {
                      maxPointWidth: 4,
                  },
                  series: {
                      fillColor: {
                          linearGradient: [0, 0, 0, 300],
                          stops: [
                              [0, '#F6C30D'],
                              [
                                  1,
                                  Highcharts.color(
                                      Highcharts.getOptions().colors[0],
                                  )
                                      .setOpacity(0)
                                      .get('rgba'),
                              ],
                          ],
                      },
                  },
              },
              yAxis: [
                  {
                      gridLineWidth: 0,
                      offset: 0,
                      opposite: false,
                      title: {
                          offset: 0,
                          style: {
                              color: '#FFFFFF',
                              'font-family': 'SANS',
                              'font-size': '12px',
                              width: 'auto',
                          },
                          rotation: 0,
                          y: -20,
                          align: 'high',
                          text: 'Sales',
                      },
                      max: getMaxSales(
                          revenueHistory.map((item) => parseInt(item['sales'])),
                      ),
                      labels: {
                          style: {
                              color: '#FFFFFF',
                              'font-family': 'SANS',
                              'font-size': '11px',
                          },
                          formatter: function () {
                              return formatNumber(this.value)
                          },
                      },
                      showFirstLabel: false,
                  },
                  {
                      offset: 0,
                      maxPadding: 0,
                      opposite: true,
                      title: {
                          offset: 0,
                          style: {
                              color: '#F6C30D',
                              'font-family': 'SANS',
                              'font-size': '12px',
                              width: '100px',
                          },
                          rotation: 0,
                          y: -20,
                          align: 'high',
                          text: currency === 'usd' ? 'Volume' : 'Volume (WAX)',
                      },
                      labels: {
                          style: {
                              color: '#F6C30D',
                              'font-family': 'SANS',
                              'font-size': '11px',
                          },
                          formatter: function () {
                              return currency === 'wax'
                                  ? formatNumber(this.value)
                                  : formatUSDM(this.value)
                          },
                      },
                      showFirstLabel: false,
                  },
              ],
              xAxis: {
                  categories: revenueHistory
                      ? revenueHistory.map((item) => item['date'])
                      : [],
                  labels: {
                      style: {
                          color: '#FEFEFE',
                          'font-family': 'SANS',
                          'font-size': '11px',
                      },
                      align: 'center',
                      step: chartRange / 3,
                  },
              },
              tooltip: {
                  shared: true,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  style: {
                      color: '#FEFEFE',
                      'font-family': 'SANS',
                      'font-size': '11px',
                  },
                  formatter: function () {
                      return this.points.reduce(function (s, point) {
                          return (
                              s +
                              '<br/>' +
                              point.series.name +
                              ': ' +
                              point.series.tooltipOptions.valuePrefix +
                              formatNumber(point.y) +
                              point.series.tooltipOptions.valueSuffix
                          )
                      }, '<b>' + this.x + '</b>')
                  },
              },
              series: [
                  {
                      type: 'column',
                      borderWidth: 0,
                      name: 'Sales',
                      color: '#F6C30D',
                      data: revenueHistory.map((item) => [
                          item['date'],
                          item['sales'],
                      ]),
                      yAxis: 0,
                      tooltip: {
                          valueSuffix: '',
                          valuePrefix: '',
                      },
                  },
                  {
                      type: 'areaspline',
                      borderWidth: 0,
                      name: 'Volume',
                      color: '#F6C30D',
                      data: revenueHistory.map((item) => [
                          item['date'],
                          item[currency === 'wax' ? 'waxVolume' : 'usdVolume'],
                      ]),
                      tooltip: {
                          valueSuffix: currency === 'wax' ? ' WAX' : '',
                          valuePrefix: currency === 'usd' ? '$' : '',
                          fontSize: 10,
                      },
                      yAxis: 1,
                  },
              ],
              legend: {
                  height: 100,
                  itemStyle: {
                      color: '#FEFEFE',
                      overflowY: 'auto',
                      maxHeight: 100,
                  },
              },
              credits: {
                  enabled: false,
              },
          }
        : null

    return (
        <div>
            <div className={'mb-8'}>
                {!embedded ? (
                    <div className="w-52 my-2">
                        <Select
                            fullWidth
                            size="small"
                            value={defaultCurrencyOption.value}
                            onChange={selectCurrency}
                            disabled={isLoading}
                        >
                            {currencyDropDownOptions.map((option) => (
                                <MenuItem value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                ) : null}
                {!embedded ? (
                    <div className="w-52 my-2">
                        <Select
                            fullWidth
                            size="small"
                            value={defaultChartOption.value}
                            onChange={onSelectChartRange}
                            disabled={isLoading}
                        >
                            {chartDropDownOptions.map((option) => (
                                <MenuItem value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                ) : null}
            </div>
            {isLoading ? (
                <LoadingIndicator />
            ) : (
                <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                />
            )}
        </div>
    )
}

export default Chart
